import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_REPORTINGTAG_LOAD,
    ACTION_REPORTINGTAG_LOAD_BY_ID,
    ACTION_REPORTINGTAG_LOAD_FAILURE,
    ACTION_REPORTINGTAG_LOAD_SUCCESS,

    ACTION_REPORTINGTAG_SAVE,
    ACTION_REPORTINGTAG_SAVE_SUCCESS,
    ACTION_REPORTINGTAG_SAVE_FAILURE,

    ACTION_REPORTINGTAG_CREATE,
    ACTION_REPORTINGTAG_CREATE_SUCCESS,
    ACTION_REPORTINGTAG_CREATE_FAILURE,

    ACTION_REPORTINGTAG_VALIDATE,
    ACTION_REPORTINGTAG_VALIDATE_SUCCESS,
    ACTION_REPORTINGTAG_VALIDATE_FAILURE,

    ACTION_REPORTINGTAG_CLEAR,
    ACTION_REPORTINGTAG_FILTER,

    ReportingTagAction
} from '../actions/reportingTags';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface IReportingTagState {
    formData: Dtos.ReportingTag | Dtos.ReportingTag[];
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[];
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    validateState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.ReportingTag[];
}

const initialState: IReportingTagState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: []
};


function loading(state: IReportingTagState, _isLoading: boolean): IReportingTagState {
    const newState: IReportingTagState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(state: IReportingTagState,
    formData: Dtos.ReportingTag | Dtos.ReportingTag[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IReportingTagState {
    const newState: IReportingTagState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function filter(state: IReportingTagState, formFilter: IFormFilter): IReportingTagState {

    const interests: Dtos.ReportingTag[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formFilteredData = interests ? interests.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" || p.value.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    const newState: IReportingTagState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        }
    });

    return newState;
}

function loadFailure(state: IReportingTagState, responseStatus: Dtos.ResponseStatus): IReportingTagState {
    const newState: IReportingTagState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function clear(state: IReportingTagState, propertiesToClear?: string[]): IReportingTagState {

    if (!propertiesToClear) {
        let formFilter = state.formFilter;
        const newState: IReportingTagState = update(initialState, {
            formFilter: {
                $set: formFilter
            }
        });
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: IReportingTagState, _isSaving: boolean): IReportingTagState {
    const newState: IReportingTagState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IReportingTagState,
    formData: Dtos.ReportingTag | Dtos.ReportingTag[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IReportingTagState {
    const newState: IReportingTagState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: IReportingTagState, responseStatus: Dtos.ResponseStatus): IReportingTagState {
    const newState: IReportingTagState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: IReportingTagState, _isCreating: boolean): IReportingTagState {
    const newState: IReportingTagState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: IReportingTagState,
    formData: Dtos.ReportingTag,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): IReportingTagState {
    const newState: IReportingTagState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: IReportingTagState, responseStatus: Dtos.ResponseStatus): IReportingTagState {
    const newState: IReportingTagState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function validating(state: IReportingTagState, _isValidating: boolean): IReportingTagState {
    const newState: IReportingTagState = update(state, {
        validatingState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: IReportingTagState,
    formData: Dtos.ReportingTag,
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IReportingTagState {
    const newState: IReportingTagState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: IReportingTagState, responseStatus: Dtos.ResponseStatus): IReportingTagState {
    const newState: IReportingTagState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

const ReportingTagReducer: Reducer<IReportingTagState> = (state: IReportingTagState = initialState, action: ReportingTagAction) => {
    switch (action.type) {
        case ACTION_REPORTINGTAG_LOAD:
        case ACTION_REPORTINGTAG_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_REPORTINGTAG_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_REPORTINGTAG_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_REPORTINGTAG_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_REPORTINGTAG_SAVE:
            return saving(state, true);
        case ACTION_REPORTINGTAG_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_REPORTINGTAG_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_REPORTINGTAG_CREATE:
            return creating(state, true);
        case ACTION_REPORTINGTAG_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_REPORTINGTAG_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_REPORTINGTAG_VALIDATE:
            return validating(state, true);
        case ACTION_REPORTINGTAG_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_REPORTINGTAG_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_REPORTINGTAG_FILTER:
            return filter(state, action.formFilter);

    }

    return state;
}

export default ReportingTagReducer;